import { useCallback, useEffect, useRef } from 'react';

import { type AudioDeviceInfo, useChunkUpload } from '@repo/videa-notes';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { createDebugLogger } from '@videa/utils';

import type { NoteGenerationErrorProps, VideaNotesUIState } from '../types';

import type { UploadErrorMeta } from './useAudioUpload';
import { useSynapseAudioUpload } from './useSynapseAudioUpload';

const debug = createDebugLogger('SynapseUploadIntegration');

interface UseSynapseUploadIntegrationProps {
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  /** EstimatedVisit.id from the appointment data */
  estimatedVisitId: number | null | undefined;
  /** Patient's external ID (PmsPatient.pmsId || ImagingPatient.practicePatientId || chartNum) */
  externalPatientId: string | undefined;
  /** Patient's PMS ID (PmsPatient.videaId) */
  pmsPatientId: string | undefined;
  /** Patient's imaging ID (ImagingPatient.videaId) */
  imagingPatientId: string | undefined;
  /** BCP 47 language tag for the recording (e.g. 'en', 'es') */
  selectedLanguage: string;
  /** Patient name — sent as top-level field, encrypted server-side */
  patientName: string | undefined;
  currentOrganizationId: string;
  currentPracticeId: string;
  patientConsentRecordId: string | undefined;
  selectedPrompt: string | undefined;
  selectedTemplate: string | undefined;
  /** Available audio devices during recording */
  availableAudioDevices: AudioDeviceInfo[] | undefined;
  /** Selected audio device ID */
  selectedAudioDeviceId: string | undefined;
  enableVoiceNotesConsent: boolean;
  enableVoiceNotesCustomTemplates: boolean;
  refetchClinicalNotes: () => Promise<{ data: ClinicalNote[] | undefined }>;
  refetchStandalonePatientVisitNotes: () => Promise<{
    data: ClinicalNote[] | undefined;
  }>;
  setUiStatus: (status: VideaNotesUIState) => void;
  setIsSheetOpen: (isOpen: boolean) => void;
  onSetSelectedNoteId: (noteId: string | undefined) => void;
  onGenerateSuccess: (generationTime: number) => void;
  onUploadError: (errorMeta: UploadErrorMeta) => void;
  onUploadSuccess: (s3UploadTime: number) => void;
}

interface StartAmbientSessionOptions {
  /** Consent record ID to use (avoids React state timing issues) */
  consentRecordId?: string;
  /** OPFS session ID to use (avoids React state timing issues) */
  opfsSessionId?: string;
  /** Recovered ambient session ID to reuse (for session recovery) */
  recoveredAmbientSessionId?: string;
}

interface UseSynapseUploadIntegrationReturn {
  /** Processing error from Synapse upload */
  processingError: NoteGenerationErrorProps | undefined;
  /**
   * Start ambient session when recording begins.
   *
   * @param options - Optional overrides to avoid React state timing issues
   */
  startAmbientSession: (
    options?: StartAmbientSessionOptions,
  ) => Promise<string | null>;
  /** Finish recording and trigger note generation */
  finishRecording: () => Promise<void>;
  /** Upload a file (blob URL) as a single chunk via Synapse — for devtools file upload flow */
  uploadFileViaSynapse: (blobUrl: string) => Promise<void>;
  /** Whether synapse upload is properly configured */
  isConfigured: boolean;
}

/**
 * Hook that integrates useSynapseAudioUpload with ChunkUploadContext.
 *
 * When enabled, this hook:
 * 1. Registers the queueChunkForUpload function with ChunkUploadContext
 * 2. Provides startAmbientSession for when recording starts
 * 3. Provides finishRecording for when recording ends
 *
 * This bridges the gap between the recording flow (AudioRecorderProvider)
 * and the Synapse upload flow (service worker + ambient API).
 */
export const useSynapseUploadIntegration = ({
  unifiedAppointmentId,
  standalonePatientVisitId,
  estimatedVisitId,
  externalPatientId,
  pmsPatientId,
  imagingPatientId,
  selectedLanguage,
  patientName,
  currentOrganizationId,
  currentPracticeId,
  patientConsentRecordId,
  selectedPrompt,
  selectedTemplate,
  availableAudioDevices,
  selectedAudioDeviceId,
  enableVoiceNotesConsent,
  enableVoiceNotesCustomTemplates,
  refetchClinicalNotes,
  refetchStandalonePatientVisitNotes,
  setUiStatus,
  setIsSheetOpen,
  onSetSelectedNoteId,
  onGenerateSuccess,
  onUploadError,
  onUploadSuccess,
}: UseSynapseUploadIntegrationProps): UseSynapseUploadIntegrationReturn => {
  const { setQueueChunkUpload } = useChunkUpload();
  const isRegisteredRef = useRef(false);

  const {
    processingError,
    startAmbientSession,
    queueChunkForUpload,
    finishRecording,
    uploadFileViaSynapse,
    isConfigured,
  } = useSynapseAudioUpload({
    unifiedAppointmentId,
    standalonePatientVisitId,
    estimatedVisitId,
    externalPatientId,
    pmsPatientId,
    imagingPatientId,
    selectedLanguage,
    patientName,
    currentOrganizationId,
    currentPracticeId,
    patientConsentRecordId,
    selectedPrompt,
    selectedTemplate,
    availableAudioDevices,
    selectedAudioDeviceId,
    enableVoiceNotesConsent,
    enableVoiceNotesCustomTemplates,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
    setUiStatus,
    setIsSheetOpen,
    onSetSelectedNoteId,
    onGenerateSuccess,
    onUploadError,
    onUploadSuccess,
  });

  // Register the chunk upload function with the context when configured
  useEffect(() => {
    if (isConfigured && !isRegisteredRef.current) {
      debug.log('Registering queueChunkForUpload with context');
      setQueueChunkUpload(queueChunkForUpload);
      isRegisteredRef.current = true;
    }

    // Cleanup on unmount
    return () => {
      if (isRegisteredRef.current) {
        debug.log('Unregistering queueChunkForUpload from context');
        setQueueChunkUpload(null);
        isRegisteredRef.current = false;
      }
    };
  }, [isConfigured, queueChunkForUpload, setQueueChunkUpload]);

  // Wrap startAmbientSession to no-op when not configured
  const wrappedStartAmbientSession = useCallback(
    async (options?: StartAmbientSessionOptions): Promise<string | null> => {
      if (!isConfigured) {
        debug.log('Skipping startAmbientSession - not configured');
        return null;
      }
      return startAmbientSession(options);
    },
    [isConfigured, startAmbientSession],
  );

  // Wrap finishRecording to no-op when not configured
  const wrappedFinishRecording = useCallback(async (): Promise<void> => {
    if (!isConfigured) {
      debug.log('Skipping finishRecording - not configured');
      return;
    }
    return finishRecording();
  }, [isConfigured, finishRecording]);

  // Wrap uploadFileViaSynapse to no-op when not configured
  const wrappedUploadFileViaSynapse = useCallback(
    async (blobUrl: string): Promise<void> => {
      if (!isConfigured) {
        debug.log('Skipping uploadFileViaSynapse - not configured');
        return;
      }
      return uploadFileViaSynapse(blobUrl);
    },
    [isConfigured, uploadFileViaSynapse],
  );

  return {
    processingError,
    startAmbientSession: wrappedStartAmbientSession,
    finishRecording: wrappedFinishRecording,
    uploadFileViaSynapse: wrappedUploadFileViaSynapse,
    isConfigured,
  };
};
